import { Injector } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { isUndefined, zip } from 'lodash';
import { assertDefined } from '../../../../cross-team-shared/misc-utils/strict-record';
import { Point3D } from '../../../../team-apis/otterdam/answers/shape-typings';
import { CUBE_MIN_LEGAL_SCALE } from '../../../state/cuboid-location-state-3d';
import {
  transformToLocal,
  transformToWorld,
} from '../../../utils/cuboid-utils';
import { getLegalizedScale } from '../../../utils/point-cloud-utils';
import {
  addPoints,
  mapPoint,
  normalizePoint,
  subtractPoints,
} from '../../../utils/point-utils';
import { Axis } from '../edit-panel.component';
import {
  AbstractEditPanelDragHandler,
  EditPanelMouseState,
} from './abstract-edit-panel-drag-handler';
import {
  getContainerPoint,
  getCuboidNdcBoundingBox,
  NdcBoundingBox,
} from './drag-handler-utils';
import {
  getEdgeTolerance,
  isPointWithinBottomEdge,
  isPointWithinBottomLeftCorner,
  isPointWithinBottomRightCorner,
  isPointWithinLeftEdge,
  isPointWithinRightEdge,
  isPointWithinTopEdge,
  isPointWithinTopLeftCorner,
  isPointWithinTopRightCorner,
} from './resize-utils';

type Sign = 1 | -1;

export enum Edge {
  Left = 0,
  Bottom,
  Right,
  Top,
}

export class ResizeDragHandler extends AbstractEditPanelDragHandler {
  private edges: [Edge, Edge | undefined];

  constructor(axis: Axis, injector: Injector, initialEvent: MouseEvent) {
    super(axis, injector, initialEvent);
    const cuboidBoundingBox = getCuboidNdcBoundingBox(
      this.store.getFocusedShapeLocation(),
      this.panelCamera,
    );

    const viewRegion = this.store.getViewRegionStore(this.axis).state();
    const tolerance = getEdgeTolerance(viewRegion, this.panelCamera);
    const corner = getCorner(initialEvent, cuboidBoundingBox, tolerance);

    this.edges = corner || [
      getEdge(initialEvent, cuboidBoundingBox, tolerance),
      undefined,
    ];
  }

  protected override getTrackEvent() {
    return {
      method: 'resize',
      sub_method: isDefined(this.edges[1]) ? 'corner_resize' : 'edge_resize',
    };
  }

  protected onDrag(currentMouseState: EditPanelMouseState) {
    const { scale: initialScale, position: initialPosition } =
      this.initialLocation;

    const resizeDelta = this.getResizeDelta(currentMouseState);
    const newScale = getLegalizedScale(addPoints(initialScale, resizeDelta));

    const translation = this.getTranslation(resizeDelta);
    const newPosition = addPoints(initialPosition, translation);

    const locationStore = this.store.getFocusedCuboidLocationStore();
    locationStore.getRootStore().batch(() => {
      locationStore('scale').set(newScale);
      locationStore('position').set(newPosition);
    });
  }

  private getTranslation(resizeDelta: Point3D) {
    const growthSignForEdges = [
      getGrowthSignForEdge(this.axis, this.edges[0]),
      getGrowthSignForEdge(this.axis, this.edges[1]),
    ];

    const cuboidEdgeAxes = [
      getCuboidEdgeAxis(this.axis, this.edges[0]),
      getCuboidEdgeAxis(this.axis, this.edges[1]),
    ];

    const localTranslation: Point3D = [0, 0, 0];
    zip(cuboidEdgeAxes, growthSignForEdges).forEach(
      ([cuboidEdgeAxis, growthSignForEdge]) => {
        if (isUndefined(cuboidEdgeAxis) || isUndefined(growthSignForEdge)) {
          return;
        }

        localTranslation[cuboidEdgeAxis] =
          resizeDelta[cuboidEdgeAxis] * 0.5 * growthSignForEdge;
      },
    );

    return transformToWorld(localTranslation, this.initialLocation);
  }

  private getResizeDelta(currentMouseState: EditPanelMouseState) {
    const worldDelta = subtractPoints(
      currentMouseState.world,
      this.initialMouseState.world,
    );

    const localDelta = transformToLocal(worldDelta, this.initialLocation);

    const cuboidEdgeAxes = [
      getCuboidEdgeAxis(this.axis, this.edges[0]),
      getCuboidEdgeAxis(this.axis, this.edges[1]),
    ];
    const growthSigns = [
      getGrowthSign(this.axis, this.edges[0], localDelta),
      getGrowthSign(this.axis, this.edges[1], localDelta),
    ];

    const resizeDelta: Point3D = [0, 0, 0];
    zip(cuboidEdgeAxes, growthSigns).forEach(([cuboidEdgeAxis, growthSign]) => {
      if (isUndefined(cuboidEdgeAxis) || isUndefined(growthSign)) {
        return;
      }

      const maximumShrinkingAllowed = -(
        this.initialLocation.scale[cuboidEdgeAxis] - CUBE_MIN_LEGAL_SCALE
      );

      const positiveDelta = mapPoint(localDelta, Math.abs);
      resizeDelta[cuboidEdgeAxis] = Math.max(
        positiveDelta[cuboidEdgeAxis] * growthSign,
        maximumShrinkingAllowed,
      );
    });

    return resizeDelta;
  }
}

function getCuboidEdgeAxis(
  panelAxis: Axis,
  edge: Edge | undefined,
): Axis | undefined {
  if (isUndefined(edge)) {
    return;
  }

  const isHorizontalEdge = [Edge.Left, Edge.Right].includes(edge);

  if (isHorizontalEdge) {
    return panelAxis === 0 ? 1 : 0;
  } else if (panelAxis === 2) {
    return 1;
  }

  return 2;
}

function getGrowthSignForEdge(
  panelAxis: Axis,
  edge: Edge | undefined,
): Sign | undefined {
  if (isUndefined(edge)) {
    return undefined;
  }

  if (panelAxis === 2) {
    return [Edge.Left, Edge.Bottom].includes(edge) ? 1 : -1;
  }
  return [Edge.Left, Edge.Top].includes(edge) ? 1 : -1;
}

function getGrowthSign(
  axis: Axis,
  edge: Edge | undefined,
  localDifference: Point3D,
): Sign | undefined {
  if (isUndefined(edge)) {
    return;
  }

  const isResizingVertically = [Edge.Top, Edge.Bottom].includes(edge);
  const ndcPositiveEdge = isResizingVertically ? Edge.Top : Edge.Right;
  const cuboidAxisSignAtNdcPositive = getGrowthSignForEdge(
    axis,
    ndcPositiveEdge,
  );
  assertDefined(cuboidAxisSignAtNdcPositive);

  const mouseDirection = normalizePoint(localDifference);
  const resizingDirection = getCuboidEdgeAxis(axis, edge);
  assertDefined(resizingDirection);

  const resizeDragSign: Sign =
    mouseDirection[resizingDirection] * cuboidAxisSignAtNdcPositive >= 0
      ? 1
      : -1;

  const cuboidResizingSign = resizeDragSign * cuboidAxisSignAtNdcPositive;
  const isGrowing = cuboidResizingSign === getGrowthSignForEdge(axis, edge);

  return isGrowing ? 1 : -1;
}

function getCorner(
  event: MouseEvent,
  cuboidBoundingBox: NdcBoundingBox,
  tolerance: number,
): [Edge, Edge] | undefined {
  const mouse = getContainerPoint(event);

  if (isPointWithinTopLeftCorner(mouse, cuboidBoundingBox, tolerance)) {
    return [Edge.Left, Edge.Top];
  }
  if (isPointWithinTopRightCorner(mouse, cuboidBoundingBox, tolerance)) {
    return [Edge.Right, Edge.Top];
  }
  if (isPointWithinBottomLeftCorner(mouse, cuboidBoundingBox, tolerance)) {
    return [Edge.Left, Edge.Bottom];
  }
  if (isPointWithinBottomRightCorner(mouse, cuboidBoundingBox, tolerance)) {
    return [Edge.Right, Edge.Bottom];
  }
  return undefined;
}

function getEdge(
  event: MouseEvent,
  cuboidBoundingBox: NdcBoundingBox,
  tolerance: number,
) {
  const mouse = getContainerPoint(event);

  if (isPointWithinLeftEdge(mouse, cuboidBoundingBox, tolerance)) {
    return Edge.Left;
  }
  if (isPointWithinBottomEdge(mouse, cuboidBoundingBox, tolerance)) {
    return Edge.Bottom;
  }
  if (isPointWithinRightEdge(mouse, cuboidBoundingBox, tolerance)) {
    return Edge.Right;
  }
  if (isPointWithinTopEdge(mouse, cuboidBoundingBox, tolerance)) {
    return Edge.Top;
  }
  throw new Error('Mouse must be over at least one edge');
}
